import { useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { resendEmail } from "../../../shared/utils/helpers";
import {
  clearShowResendMessage,
  fetchLogsThunk,
  resetLogFilters,
  selectDeliveryStatusFilter,
  selectFilteredLogs,
  selectJobStatusOptions,
  selectLogs,
  selectLogsCurrentPage,
  selectLogsDateRange,
  selectLogsEndDate,
  selectLogsLoading,
  selectLogsSearchQuery,
  selectLogsShowFilterModal,
  selectLogsStartDate,
  selectLogsSpreadTable,
  selectShowResendMessage,
  setCurrentPage,
  setDateRange,
  setDeliveryStatusFilter,
  setEndDate,
  setSearchQuery,
  setShowFilterModal,
  setShowResendMessage,
  setSpreadTable,
  setStartDate,
} from "../store/logsSlice";


const getJobStatusColor = (status) => {
  switch (status) {
    case "PROCESS_COMPLETED":
      return "#d4edda";
    case "EMAIL_FAILED":
      return "#f8d7da"; 
    case "UPLOADED":
      return "#fff3cd";
    default:
      return "#d4edda";
  }
};

/**
 * @returns {Object}
 */
export const useLogs = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const logs = useSelector(selectLogs);
  const filteredLogs = useSelector(selectFilteredLogs);
  const isLoading = useSelector(selectLogsLoading);
  const currentPage = useSelector(selectLogsCurrentPage);
  const showResendMessage = useSelector(selectShowResendMessage);
  const startDate = useSelector(selectLogsStartDate);
  const endDate = useSelector(selectLogsEndDate);
  const jobStatusOptions = useSelector(selectJobStatusOptions);
  const deliveryStatusFilter = useSelector(selectDeliveryStatusFilter);
  const searchQuery = useSelector(selectLogsSearchQuery);
  const dateRange = useSelector(selectLogsDateRange);
  const spreadTable = useSelector(selectLogsSpreadTable);
  const showFilterModal = useSelector(selectLogsShowFilterModal);

  const resolvedClientId = useMemo(
    () => Number(currentUser?.client_id || 0) || null,
    [currentUser?.client_id],
  );

  const resendEmailHandlerRef = useRef(null);

  const processGetLogs = useCallback(async () => {
    if (!resolvedClientId) {
      return;
    }
    await dispatch(fetchLogsThunk(resolvedClientId));
  }, [dispatch, resolvedClientId]);



  /**
   * Handle resend email action
   */
  const resendEmailHandler = useCallback(async (email) => {
    dispatch(setShowResendMessage({
      status: true,
      message: "Sending email...",
    }));

    if (!email.email_from_name) {
      dispatch(setShowResendMessage({
        status: true,
        message: "Cannot resend: missing email from name",
      }));
      setTimeout(() => {
        dispatch(clearShowResendMessage());
      }, 5000);
      return;
    }

    try {
      await resendEmail(
        email.email_from_name,
        email.email_from_address,
        email.received_header,
        email.email_body,
        email.job_name,
        email.voicemail,
      );
      dispatch(setShowResendMessage({
        status: true,
        message: "Email resent successfully!",
      }));

      setTimeout(() => {
        dispatch(clearShowResendMessage());
      }, 5000);
    } catch (error) {
      console.error("[Logs -> Resend Email]: Error ->", error);
      dispatch(setShowResendMessage({
        status: true,
        message: "Failed to resend email",
      }));
      setTimeout(() => {
        dispatch(clearShowResendMessage());
      }, 5000);
    }
  }, [dispatch]);

  // Update ref when handler changes
  useEffect(() => {
    resendEmailHandlerRef.current = resendEmailHandler;
  }, [resendEmailHandler]);

  /**
   * Handle clear search - reset all filters and reload logs
   */
  const handleClearSearch = useCallback(() => {
    dispatch(resetLogFilters());
    processGetLogs();
  }, [dispatch, processGetLogs]);

  /**
   * Handle close resend message
   */
  const handleCloseResendMessage = useCallback(() => {
    dispatch(clearShowResendMessage());
  }, [dispatch]);

  // Refresh logs whenever this page is opened or active client scope changes.
  useEffect(() => {
    processGetLogs();
  }, [processGetLogs]);

  const setCurrentPageValue = useCallback(
    (value) => {
      dispatch(setCurrentPage(value));
    },
    [dispatch],
  );

  const setStartDateValue = useCallback(
    (value) => {
      dispatch(setStartDate(value));
    },
    [dispatch],
  );

  const setEndDateValue = useCallback(
    (value) => {
      dispatch(setEndDate(value));
    },
    [dispatch],
  );

  const setDeliveryStatusFilterValue = useCallback(
    (value) => {
      dispatch(setDeliveryStatusFilter(value));
    },
    [dispatch],
  );

  const setSearchQueryValue = useCallback(
    (value) => {
      dispatch(setSearchQuery(value));
    },
    [dispatch],
  );

  const setDateRangeValue = useCallback(
    (value) => {
      dispatch(setDateRange(value));
    },
    [dispatch],
  );

  const setSpreadTableValue = useCallback(
    (value) => {
      const resolvedValue =
        typeof value === "function" ? value(spreadTable) : value;
      dispatch(setSpreadTable(resolvedValue));
    },
    [dispatch, spreadTable],
  );

  const setShowFilterModalValue = useCallback(
    (value) => {
      dispatch(setShowFilterModal(value));
    },
    [dispatch],
  );

  const startDateValue = useMemo(
    () => (startDate ? new Date(startDate) : null),
    [startDate],
  );

  const endDateValue = useMemo(
    () => (endDate ? new Date(endDate) : null),
    [endDate],
  );

  return {
    // State
    logs,
    filteredLogs,
    isLoading,
    currentPage,
    setCurrentPage: setCurrentPageValue,
    showResendMessage,
    startDate: startDateValue,
    setStartDate: setStartDateValue,
    endDate: endDateValue,
    setEndDate: setEndDateValue,
    jobStatusOptions,
    deliveryStatusFilter,
    setDeliveryStatusFilter: setDeliveryStatusFilterValue,
    searchQuery,
    setSearchQuery: setSearchQueryValue,
    dateRange,
    setDateRange: setDateRangeValue,
    spreadTable,
    setSpreadTable: setSpreadTableValue,
    showFilterModal,
    setShowFilterModal: setShowFilterModalValue,

    // Handlers
    handleClearSearch,
    resendEmailHandler,
    handleCloseResendMessage,

    // Utilities
    getJobStatusColor,
    resendEmailHandlerRef,
  };
};
